import React, { useState, useRef, useEffect } from 'react'

import { Play, Pause, Volume2 } from 'lucide-react'
import WaveSurfer from 'wavesurfer.js'
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js'

import { Button } from '@/shared/components/atoms/Button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Slider } from '@/shared/components/atoms/Slider'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

import { MUSIC_GENRE_GROUPS, getMusicGenreUrl } from '../constants/musicGenres'

// These must stay in sync with CreateAICampaignPage
const MUSIC_PADDING_BEFORE = 1.5
const MUSIC_PADDING_AFTER = 3.0

interface EditAudioModalProps {
	isOpen: boolean
	onClose: () => void
	voiceAudioUrl: string
	voiceAudioBlob: Blob
	initialMusicGroup?: string
	initialMusicGenre?: string
	initialMusicStart?: number
	initialMusicEnd?: number
	onSave: (
		musicGroup: string,
		musicGenre: string,
		musicStart: number,
		musicEnd: number,
		voiceVolume: number,
		musicVolume: number
	) => void
}

export const EditAudioModal: React.FC<EditAudioModalProps> = ({
	isOpen,
	onClose,
	voiceAudioUrl,
	initialMusicGroup = '',
	initialMusicGenre = '',
	initialMusicStart,
	initialMusicEnd,
	onSave,
}) => {
	const [selectedMusicGroup, setSelectedMusicGroup] =
		useState(initialMusicGroup)
	const [selectedMusicGenre, setSelectedMusicGenre] =
		useState(initialMusicGenre)
	const [voiceVolume, setVoiceVolume] = useState([100]) // Default 100% voice
	const [musicVolume, setMusicVolume] = useState([40]) // Default 40% music
	const [isPlayingVoice, setIsPlayingVoice] = useState(false)
	const [isPlayingMusic, setIsPlayingMusic] = useState(false)
	const [voiceDuration, setVoiceDuration] = useState(0)
	const [voiceCurrentTime, setVoiceCurrentTime] = useState(0)
	const [musicDuration, setMusicDuration] = useState(0)
	const [musicCurrentTime, setMusicCurrentTime] = useState(0)
	const [musicRegion, setMusicRegion] = useState<{
		start: number
		end: number
	} | null>(null)

	const voiceWaveformRef = useRef<HTMLDivElement>(null)
	const musicWaveformRef = useRef<HTMLDivElement>(null)
	const voiceWavesurferRef = useRef<WaveSurfer | null>(null)
	const musicWavesurferRef = useRef<WaveSurfer | null>(null)
	const regionsPluginRef = useRef<any>(null)

	useEffect(() => {
		if (initialMusicGroup && initialMusicGenre) {
			setSelectedMusicGroup(initialMusicGroup)
			setSelectedMusicGenre(initialMusicGenre)
		}
	}, [initialMusicGroup, initialMusicGenre, isOpen])

	useEffect(() => {
		if (!isOpen) return

		const timer = setTimeout(() => {
			if (!voiceWaveformRef.current) {
				return
			}

			const wavesurfer = WaveSurfer.create({
				container: voiceWaveformRef.current,
				waveColor: '#808080',
				progressColor: '#ffffff',
				cursorColor: '#ffffff',
				cursorWidth: 0,
				barWidth: 2,
				barGap: 1,
				barRadius: 3,
				height: 48,
				normalize: true,
				backend: 'WebAudio',
				interact: true,
				hideScrollbar: true,
				barHeight: 1,
			})

			wavesurfer.load(voiceAudioUrl)

			wavesurfer.on('ready', () => {
				setVoiceDuration(wavesurfer.getDuration())
			})

			wavesurfer.on('error', (error) => {
				console.error('EditAudioModal - WaveSurfer error:', error)
			})

			wavesurfer.on('audioprocess', () => {
				setVoiceCurrentTime(wavesurfer.getCurrentTime())
			})

			wavesurfer.on('play', () => {
				setIsPlayingVoice(true)
			})

			wavesurfer.on('pause', () => {
				setIsPlayingVoice(false)
			})

			wavesurfer.on('finish', () => {
				setIsPlayingVoice(false)
				wavesurfer.seekTo(0)
				if (musicWavesurferRef.current) {
					musicWavesurferRef.current.pause()
					musicWavesurferRef.current.seekTo(0)
				}
			})

			voiceWavesurferRef.current = wavesurfer
		}, 100)

		return () => {
			clearTimeout(timer)
			if (voiceWavesurferRef.current) {
				voiceWavesurferRef.current.destroy()
				voiceWavesurferRef.current = null
			}
		}
	}, [isOpen, voiceAudioUrl])

	useEffect(() => {
		if (!isOpen || !selectedMusicGenre) return

		const musicUrl = getMusicGenreUrl(selectedMusicGenre)

		if (!musicUrl) {
			return
		}

		const timer = setTimeout(() => {
			if (!musicWaveformRef.current) {
				return
			}

			const regions = RegionsPlugin.create()
			regionsPluginRef.current = regions

			const wavesurfer = WaveSurfer.create({
				container: musicWaveformRef.current,
				waveColor: '#808080',
				progressColor: '#ffffff',
				cursorColor: '#ffffff',
				cursorWidth: 0,
				barWidth: 2,
				barGap: 1,
				barRadius: 3,
				height: 48,
				normalize: true,
				backend: 'WebAudio',
				interact: true,
				hideScrollbar: true,
				barHeight: 1,
				plugins: [regions],
			})

			wavesurfer.load(musicUrl)

			wavesurfer.on('ready', () => {
				const duration = wavesurfer.getDuration()
				setMusicDuration(duration)

				const totalWindow =
					voiceDuration + MUSIC_PADDING_BEFORE + MUSIC_PADDING_AFTER

				const hasInitialRegion =
					typeof initialMusicStart === 'number' &&
					typeof initialMusicEnd === 'number' &&
					initialMusicEnd > initialMusicStart

				let regionStart: number
				let regionEnd: number

				if (hasInitialRegion) {
					// Restore previously chosen total window
					regionStart = Math.max(0, Math.min(initialMusicStart!, duration))
					regionEnd = Math.max(
						regionStart,
						Math.min(initialMusicEnd!, duration)
					)
				} else {
					// Default: center a fixed-length window (voice + paddings) in the track
					const desiredLength = Math.min(totalWindow || duration, duration)
					regionStart = Math.max(0, (duration - desiredLength) / 2)
					regionEnd = Math.min(duration, regionStart + desiredLength)
				}

				const fixedLength = regionEnd - regionStart

				const region = regions.addRegion({
					start: regionStart,
					end: regionEnd,
					color: 'rgba(124, 58, 237, 0.4)',
					drag: true,
					resize: false,
					minLength: fixedLength,
					maxLength: fixedLength,
				})

				setMusicRegion({ start: regionStart, end: regionEnd })

				region.on('update-end', () => {
					// Length is fixed by minLength/maxLength; just persist new position
					setMusicRegion({
						start: region.start,
						end: region.end,
					})
				})
			})

			wavesurfer.on('audioprocess', () => {
				setMusicCurrentTime(wavesurfer.getCurrentTime())
			})

			wavesurfer.on('play', () => {
				setIsPlayingMusic(true)
			})

			wavesurfer.on('pause', () => {
				setIsPlayingMusic(false)
			})

			wavesurfer.on('finish', () => {
				setIsPlayingMusic(false)
				wavesurfer.seekTo(0)
			})

			wavesurfer.on('error', (error) => {
				console.error('EditAudioModal - Music WaveSurfer error:', error)
			})

			musicWavesurferRef.current = wavesurfer
		}, 100)

		return () => {
			clearTimeout(timer)
			if (musicWavesurferRef.current) {
				musicWavesurferRef.current.destroy()
				musicWavesurferRef.current = null
			}
			if (regionsPluginRef.current) {
				regionsPluginRef.current = null
			}
		}
	}, [
		isOpen,
		selectedMusicGenre,
		voiceDuration,
		initialMusicStart,
		initialMusicEnd,
	])

	useEffect(() => {
		if (voiceWavesurferRef.current) {
			voiceWavesurferRef.current.setVolume(voiceVolume[0] / 100)
		}
	}, [voiceVolume])

	useEffect(() => {
		if (musicWavesurferRef.current) {
			musicWavesurferRef.current.setVolume(musicVolume[0] / 100)
		}
	}, [musicVolume])

	const toggleVoicePlayback = () => {
		if (voiceWavesurferRef.current) {
			const voiceWavesurfer = voiceWavesurferRef.current
			const isCurrentlyPlaying = isPlayingVoice

			if (!isCurrentlyPlaying) {
				voiceWavesurfer.play()

				if (musicWavesurferRef.current && musicRegion) {
					const musicWavesurfer = musicWavesurferRef.current
					musicWavesurfer.setTime(musicRegion.start)
					musicWavesurfer.play()

					const checkMusicTime = setInterval(() => {
						if (musicWavesurfer.getCurrentTime() >= musicRegion.end) {
							musicWavesurfer.pause()
							clearInterval(checkMusicTime)
						}
					}, 50)
				}
			} else {
				voiceWavesurfer.pause()
				if (musicWavesurferRef.current) {
					musicWavesurferRef.current.pause()
				}
			}
		}
	}

	const toggleMusicPlayback = () => {
		if (musicWavesurferRef.current) {
			const wavesurfer = musicWavesurferRef.current

			if (musicRegion && !isPlayingMusic) {
				wavesurfer.setTime(musicRegion.start)
				wavesurfer.play()

				const checkTime = setInterval(() => {
					if (wavesurfer.getCurrentTime() >= musicRegion.end) {
						wavesurfer.pause()
						clearInterval(checkTime)
					}
				}, 100)
			} else {
				wavesurfer.playPause()
			}
		}
	}

	const handleSave = () => {
		if (musicRegion) {
			onSave(
				selectedMusicGroup,
				selectedMusicGenre,
				musicRegion.start,
				musicRegion.end,
				voiceVolume[0] / 100,
				musicVolume[0] / 100
			)
		}
		onClose()
	}

	const availableMusicGenres = React.useMemo(() => {
		if (!selectedMusicGroup) return []
		const group = MUSIC_GENRE_GROUPS.find((g) => g.label === selectedMusicGroup)
		return group?.genres || []
	}, [selectedMusicGroup])

	const formatTime = (time: number): string => {
		if (isNaN(time)) return '0:00'
		const minutes = Math.floor(time / 60)
		const seconds = Math.floor(time % 60)
		return `${minutes}:${seconds.toString().padStart(2, '0')}`
	}

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-4xl text-foreground border-border">
				<DialogHeader>
					<DialogTitle className="text-2xl font-semibold">
						Edit your ad
					</DialogTitle>
				</DialogHeader>

				<div className="space-y-6 py-4">
					<div className="space-y-3">
						<div className="flex items-center justify-between">
							<h3 className="text-base font-medium">Voice</h3>
							<div className="flex items-center gap-3">
								<Volume2 className="h-4 w-4 text-muted-foreground" />
								<Slider
									value={voiceVolume}
									onValueChange={setVoiceVolume}
									min={0}
									max={100}
									step={1}
									className="w-32"
								/>
							</div>
						</div>
						<div className="bg-background border border-border rounded-lg p-4">
							<div className="flex items-center gap-3">
								<button
									onClick={toggleVoicePlayback}
									className="h-12 w-12 rounded-full dark:bg-primary text-primary-foreground dark:hover:bg-primary/90 hover:bg-primary/20 flex items-center justify-center flex-shrink-0"
								>
									{isPlayingVoice ? (
										<Pause className="h-4 w-4 fill-current" />
									) : (
										<Play className="h-4 w-4 fill-current ml-0.5" />
									)}
								</button>
								<span className="text-xs text-muted-foreground w-10">
									{formatTime(voiceCurrentTime)}
								</span>
								<div ref={voiceWaveformRef} className="flex-1 cursor-pointer" />
								<span className="text-xs text-muted-foreground w-10 text-right">
									{formatTime(voiceDuration)}
								</span>
							</div>
						</div>
					</div>

					{selectedMusicGenre && (
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<h3 className="text-base font-medium">Music</h3>
								<div className="flex items-center gap-3">
									<Volume2 className="h-4 w-4 text-muted-foreground" />
									<Slider
										value={musicVolume}
										onValueChange={setMusicVolume}
										min={0}
										max={100}
										step={1}
										className="w-32"
									/>
								</div>
							</div>
							<div className="bg-background border border-border rounded-lg p-4">
								<div className="flex items-center gap-3">
									<button
										onClick={toggleMusicPlayback}
										className="h-12 w-12 rounded-full dark:bg-primary text-primary-foreground dark:hover:bg-primary/90 hover:bg-primary/20 flex items-center justify-center flex-shrink-0"
									>
										{isPlayingMusic ? (
											<Pause className="h-4 w-4 fill-current" />
										) : (
											<Play className="h-4 w-4 fill-current ml-0.5" />
										)}
									</button>
									<span className="text-xs text-muted-foreground w-10">
										{formatTime(musicCurrentTime)}
									</span>
									<div
										ref={musicWaveformRef}
										className="flex-1 cursor-pointer"
									/>
									<span className="text-xs text-muted-foreground w-10 text-right">
										{formatTime(musicDuration)}
									</span>
								</div>
							</div>
						</div>
					)}

					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<label className="text-sm font-medium">Select music style</label>
							<Select
								value={selectedMusicGroup}
								onValueChange={(value) => {
									setSelectedMusicGroup(value)
									setSelectedMusicGenre('')
								}}
							>
								<SelectTrigger className="w-full bg-background border-border">
									<SelectValue placeholder="Select a music style" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="none">Don&apos;t use music</SelectItem>
									{MUSIC_GENRE_GROUPS.map((group) => (
										<SelectItem key={group.label} value={group.label}>
											{group.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{selectedMusicGroup && selectedMusicGroup !== 'none' && (
							<div className="space-y-2">
								<label className="text-sm font-medium">Select song</label>
								<Select
									value={selectedMusicGenre}
									onValueChange={setSelectedMusicGenre}
								>
									<SelectTrigger className="w-full bg-background border-border">
										<SelectValue placeholder="Select a song" />
									</SelectTrigger>
									<SelectContent>
										{availableMusicGenres.map((genre) => (
											<SelectItem key={genre.value} value={genre.value}>
												{genre.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}
					</div>

					<Button
						variant="outline"
						className="rounded-full border-border text-foreground hover:bg-accent"
					>
						Upload music
					</Button>
				</div>

				<div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
					<Button
						variant="outline"
						onClick={onClose}
						className="rounded-full px-6 border-border text-foreground hover:bg-accent"
					>
						Discard
					</Button>
					<Button onClick={handleSave} className="rounded-full px-6">
						Save changes
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
